export interface Account {
  id: string
  leaseSignatureId: string
  propertyCode: string
  propertyName: string
  reservationId: string
  roomNumber: string
  unitId: string
  unitType: string
  checkinDate: string
  checkoutDate: string
}

export interface AccountContext {
  account: Account
  accountId: string
  accountName: string
  reservation: Reservation
}

export interface AccountResponse {
  properties: {
    hs_object_id: string
    lease_signature_id: string
    pms_property_code: string
    pms_reservation_id: string
    property_short_name: string
    room_number: string
    unit: string
    unit_type: string
    checkin_date: string
    checkout_date: string
  }
}

export interface Amount {
  amount: number
  currency: string
}

export interface AppConfig {
  adyenClientId: string
  adyenEnvironment: string
  bluemoonFormsBase: string
  bluemoonLeaseApiBase: string
  cognitoRegion: string
  cognitoUserPoolId: string
  cognitoWebClientId: string
  environment: string
  loggingApiUrl: string
  loggingEnabled: boolean
  loggingLevel: string
  reduxDevTools: boolean
  residentPortalApiUrl: string
  residentPortalWSUrl: string
}

export interface AutoPayParams extends AutoPaySettings {
  propertyId: string
  reservationId: string
  accountReference: string
}

export interface AutoPaySettings {
  autoPayEnabled: boolean
  autoPayDayOfMonth: number
  autoPayAmount: number
}

export interface BillingStatement {
  propertyId: string
  bookingId: string
  reservationId: string
  folioId: string
  periodId: string
  periodStartDate: string
  periodEndDate: string
  dueDate: string
  transactions: Transaction[]
  periodCredited: number
  periodDebited: number
  periodBalance: number
  currentBalance: number
  pastBalance: number
  currency: string
}

export interface BillingParam {
  propertyId: string
  reservationId: string
}

export interface BillingStatus {
  date: string
  dueDate: string
  propertyId: string
  emails: string[]
  bookingId: string
  reservationId: string
  folioId: string
  currentBalance: number
  currency: string
}

export interface BillingActivity extends BillingStatus {
  dueDate: string
  currentBalance: number
  pendingBalance: number
  statementCurrentBalance: number
  statementPastBalance: number
  statementPeriodBalance: number
  transactions: Transaction[]
  isFutureMoveIn: boolean
}

export interface BluemoonPrefillData {
  email: string
  firstName: string
  lastName: string
  propertyAddress: string
}

export interface Charge {
  id: string
  serviceType: string
  name: string
  isPosted: boolean
  serviceDate: string
  created: string
  quantity: number
  amount: {
    grossAmount: number
    netAmount: number
    currency: string
  }
}

export interface DeletePaymentAccountParam {
  accountReference: string
  propertyId: string
  reservationId: string
}

export interface Folio {
  balance: Amount
  isMainFolio: boolean
  charges: Charge[]
  payments: Payment[]
}

export interface FolioResponse {
  count: number
  folios: Folio[]
}

export enum LeaseApplicationStatus {
  FormInProgress = 'Form In Progress',
  FormCompleted = 'Form Completed',
  PaymentInProgress = 'Payment In Progress',
  PaymentFailed = 'Payment Failed',
  PaymentCompleted = 'Payment Completed',
  BackgroundCheckInProgress = 'Background Check In Progress',
  BackgroundCheckCompleted = 'Background Check Completed',
  LeaseApproved = 'Lease Approved',
  LeaseRejected = 'Lease Rejected'
}

export enum LeaseApplicationType {
  Primary = 'Primary',
  CoApplicant = 'CoApplicant'
}

export interface LeaseApplication {
  id?: string
  adminFee?: number | string
  applicantEmail?: string
  applicantFirstName?: string
  applicantLastName?: string
  applicationId?: string
  applicationBackgroundId?: string
  applicationBackgroundStatus?: string
  applicationBackgroundUrl?: string
  applicationDocUrl?: string
  applicationFee?: number | string
  applicationFeePsp?: string
  applicationPrimaryId?: string
  applicationPropertyId?: string
  applicationSignatureId?: string
  applicationStatus?: LeaseApplicationStatus
  applicationType?: string
  applicationCoApplicants?: string
  leaseId?: string
  leaseMonthlyRent?: number
  leasePropertyId?: string
  leaseReservationId?: string
  leaseUnit?: string
  leaseUnitId?: string
  leaseUnitGroupId?: string
  leaseStartDate?: string
  leaseEndDate?: string
  leaseTerm?: string
  leaseSignatureDate?: string
  leaseSignatureId?: string
  leasePropertyCode?: string
  leasePropertyName?: string
  leasePropertyAddress?: string
  leasePropertyCity?: string
  leasePropertyState?: string
  leasePropertyZip?: string
  leasePropertyCountry?: string
  leaseUnitName?: string
  leaseUnitGroupCode?: string
  leaseCompanyName?: string
  closedDate?: string
}

export interface LeaseApplicationStep {
  id: number
  label: string
  complete: boolean
}

export interface LeaseApplicationState {
  currentStep: LeaseApplicationStep
  steps: LeaseApplicationStep[]
  leaseApplication: LeaseApplication
}

export interface LeaseUnitDetail {
  applicationFee: number
  monthlyRent: number
  propertyId: string
  propertyCode: string
  propertyName: string
  propertyAddress: string
  propertyCity: string
  propertyState: string
  propertyZip: string
  propertyCountry: string
  unitId: string
  unitName: string
  unitDescription: string
  unitGroupId: string
  unitGroupCode: string
  companyName: string
}

export interface LeaseUnitDetailParam {
  propertyId: string
  unitId: string
}

export interface NavLink {
  path: string
  label: string
}

export interface NavigationPaths {
  Account: string
  AddPaymentAccount: string
  AddSupportRequest: string
  Billing: string
  Home: string
  MakePayment: string
  MakePaymentResult: string
  PaymentAccounts: string
  PaymentHistory: string
  SupportRequests: string
}

export interface Payment {
  id: string
  method: string
  amount: Amount
  receipt: string
  paymentDate: string
  businessDate: string
  externalReference?: {
    merchantReference: string
    pspReference: string
  }
}

export interface PaymentAccount {
  id: string
  adyenAccountLast4: string
  adyenExpires?: string
  adyenMerchantAccount: string
  adyenOwner: string
  adyenPaymentIssuer?: string
  adyenPaymentMethod: string
  adyenRecurringReference: string
  adyenShopperEmail: string
  adyenShopperReference: string
  reservationAutoPay?: Record<string, AutoPaySettings>
}

export interface PaymentMethod {
  type: string
  [key: string]: any
}

export enum PaymentOrigin {
  LeaseApplication = 'leaseapplication',
  ResidentPortal = 'residentportal'
}

export interface PaymentParams {
  accountReference: string
  amountInCents: number
  propertyId: string
  reservationId: string
}

export interface PaymentMaxParams {
  propertyId: string
  reservationId: string
}
export interface PaymentSessionParams {
  amountInCents: number
  leaseApplicationId?: string
  reservationId: string
  propertyId: string
  paymentAccount: boolean
  paymentType: PaymentType
  paymentOrigin: PaymentOrigin
  returnUrl: string
}

export enum PaymentType {
  AccountCC = 'AccountCC',
  AccountACH = 'AccountACH',
  OneTimePaymentCC = 'OneTimeCC',
  OneTimePaymentACH = 'OneTimeACH',
  PreAuthorization = 'PreAuthorization'
}

export interface Property {
  propertyCode: string
  creditCardFee: number
}

export interface Reservation {
  id: string
  bookingId: string
  status: string
  unit?: {
    id: string
    name: string
    description: string
    unitGroupId: string
  }
  unitGroup?: {
    code: string
    description: string
    id: string
    name: string
    type: string
  }
  property: {
    id: string
    code: string
    name: string
  }
  ratePlan: {
    id: string
    code: string
    name: string
    description: string
  }
  totalGrossAmount: Amount
  arrival: string
  departure: string
  primaryGuest: {
    firstName: string
    lastName: string
    email: string
  }
  balance: Amount
  payableAmount: {
    [key: string]: Amount
  }
}

export interface Ticket {
  id: string
  created: string
  subject: string
  type: string
  status: string
}

export interface TicketParams {
  reservationId: string
  subject: string
  type: string
  description: string
  propertyId: string
  priority: string
  unitId: string
}

export interface TicketResponseItem {
  hs_object_id: string
  createdate: string
  subject: string
  multifamily_support_type: string
  stageName: string
}

export interface Transaction {
  date: string
  description: string
  amount: number
  currency: string
  flow: TransactionFlow
  granularity: TransactionGranularity
  refIds: string[]
  type: TransactionType
  serviceType?: string
  subAccountId?: string
}

export enum TransactionFlow {
  Credit = 'credit',
  Debit = 'debit'
}

export enum TransactionGranularity {
  Aggregate = 'aggregate',
  Item = 'item'
}

export enum TransactionType {
  Allowance = 'allowance',
  Charge = 'charge',
  Concession = 'concession',
  Payment = 'payment',
  Refund = 'refund',
  TransitoryCharge = 'transitoryCharge'
}

export enum GroupType {
  Resident = 'resident',
  LeaseApplicant = 'lease-applicant'
}

export interface User {
  email: string
  firstName: string
  lastName: string
  subject: string
  groups: GroupType[]
}
